/**
 * ColorGradingEffect - WebGPU-only display-referred colour grading controls.
 */

import * as THREE from 'three';
import { convert, dot, float, max, mix, screenUV, select, smoothstep, texture, uniform, vec3, vec4 } from 'three/tsl';
import { type Node, type UniformNode } from 'three/webgpu';

import { type EffectContext, IPostProcessEffect, type WebGLEffectData, type WebGPUComposeContext, type WebGPUEffectNode } from '../IPostProcessEffect.js';
import { type ColorGradingEffectConfig, DefaultPostprocessEffectConfigs, PostProcessPass, PostProcessRenderOrder } from '../PostProcessConfig.js';

const LUMA_WEIGHTS = vec3(0.2126, 0.7152, 0.0722);

function getTintColour(value: number | undefined): THREE.Color {
  return new THREE.Color(value ?? 0xffffff);
}

export class ColorGradingEffect extends IPostProcessEffect<ColorGradingEffectConfig> {
  override readonly type = PostProcessPass.ColorGrading;
  override readonly order = PostProcessRenderOrder.ColorGrading;

  private uniforms: {
    exposure: UniformNode<'float', number>;
    contrast: UniformNode<'float', number>;
    saturation: UniformNode<'float', number>;
    hueShift: UniformNode<'float', number>;
    temperature: UniformNode<'float', number>;
    tint: UniformNode<'float', number>;
    lift: UniformNode<'float', number>;
    gamma: UniformNode<'float', number>;
    gain: UniformNode<'float', number>;
    liftTint: UniformNode<'color', THREE.Color>;
    gammaTint: UniformNode<'color', THREE.Color>;
    gainTint: UniformNode<'color', THREE.Color>;
    shadowsTint: UniformNode<'color', THREE.Color>;
    shadowsStrength: UniformNode<'float', number>;
    midtonesTint: UniformNode<'color', THREE.Color>;
    midtonesStrength: UniformNode<'float', number>;
    highlightsTint: UniformNode<'color', THREE.Color>;
    highlightsStrength: UniformNode<'float', number>;
    hasExclusions: UniformNode<'bool', boolean>;
  } | null = null;

  override getDefaultConfig(): ColorGradingEffectConfig {
    return { ...DefaultPostprocessEffectConfigs[PostProcessPass.ColorGrading] };
  }

  override createWebGLEffect(_config: ColorGradingEffectConfig, _context: EffectContext): WebGLEffectData {
    return { effects: [] };
  }

  override createWebGPUNode(config: ColorGradingEffectConfig, context: EffectContext): WebGPUEffectNode {
    const defaults = this.getDefaultConfig();
    const hasExclusions = uniform(false);

    this.uniforms = {
      exposure: uniform(config.exposure ?? defaults.exposure ?? 1.0),
      contrast: uniform(config.contrast ?? defaults.contrast ?? 1.0),
      saturation: uniform(config.saturation ?? defaults.saturation ?? 1.0),
      hueShift: uniform(config.hueShift ?? defaults.hueShift ?? 0.0),
      temperature: uniform(config.temperature ?? defaults.temperature ?? 0.0),
      tint: uniform(config.tint ?? defaults.tint ?? 0.0),
      lift: uniform(config.lift ?? defaults.lift ?? 0.0),
      gamma: uniform(config.gamma ?? defaults.gamma ?? 1.0),
      gain: uniform(config.gain ?? defaults.gain ?? 1.0),
      liftTint: uniform(getTintColour(config.liftTint ?? defaults.liftTint)),
      gammaTint: uniform(getTintColour(config.gammaTint ?? defaults.gammaTint)),
      gainTint: uniform(getTintColour(config.gainTint ?? defaults.gainTint)),
      shadowsTint: uniform(getTintColour(config.shadowsTint ?? defaults.shadowsTint)),
      shadowsStrength: uniform(config.shadowsStrength ?? defaults.shadowsStrength ?? 0.0),
      midtonesTint: uniform(getTintColour(config.midtonesTint ?? defaults.midtonesTint)),
      midtonesStrength: uniform(config.midtonesStrength ?? defaults.midtonesStrength ?? 0.0),
      highlightsTint: uniform(getTintColour(config.highlightsTint ?? defaults.highlightsTint)),
      highlightsStrength: uniform(config.highlightsStrength ?? defaults.highlightsStrength ?? 0.0),
      hasExclusions,
    };

    const maskTexture = context.exclusionMaskTexture;

    return {
      node: float(0),
      compose: (outputNode, _ctx: WebGPUComposeContext) => {
        const graded = this.composeNode(outputNode) as Node<'vec4'>;
        const exclusionMaskTexture = _ctx.exclusionMaskTexture ?? maskTexture;
        if (!exclusionMaskTexture) {
          return graded;
        }

        const outputVec4 = outputNode as Node<'vec4'>;
        const maskRaw = texture(exclusionMaskTexture, screenUV).r;
        const withMask = vec4(mix(graded.rgb, outputVec4.rgb, maskRaw), outputVec4.a);
        return select(hasExclusions, withMask, graded);
      },
    };
  }

  private composeNode(outputNode: Node): Node {
    if (!this.uniforms) {
      return outputNode;
    }

    const input = outputNode as Node<'vec4'>;
    let colour = vec3(input.rgb).mul(this.uniforms.exposure);

    const midGrey = float(0.5);
    colour = colour.sub(midGrey).mul(this.uniforms.contrast).add(midGrey);

    const saturationLuma = dot(colour, LUMA_WEIGHTS);
    colour = mix(vec3(saturationLuma), colour, this.uniforms.saturation);

    const hueRadians = float(this.uniforms.hueShift).mul(Math.PI / 180);
    const hueCos = hueRadians.cos();
    const hueSin = hueRadians.sin();
    const red = colour.r
      .mul(float(0.299).add(hueCos.mul(0.701)).add(hueSin.mul(0.168)))
      .add(colour.g.mul(float(0.587).sub(hueCos.mul(0.587)).add(hueSin.mul(0.330))))
      .add(colour.b.mul(float(0.114).sub(hueCos.mul(0.114)).sub(hueSin.mul(0.497))));
    const green = colour.r
      .mul(float(0.299).sub(hueCos.mul(0.299)).sub(hueSin.mul(0.328)))
      .add(colour.g.mul(float(0.587).add(hueCos.mul(0.413)).add(hueSin.mul(0.035))))
      .add(colour.b.mul(float(0.114).sub(hueCos.mul(0.114)).add(hueSin.mul(0.292))));
    const blue = colour.r
      .mul(float(0.299).sub(hueCos.mul(0.300)).add(hueSin.mul(1.250)))
      .add(colour.g.mul(float(0.587).sub(hueCos.mul(0.588)).sub(hueSin.mul(1.050))))
      .add(colour.b.mul(float(0.114).add(hueCos.mul(0.886)).sub(hueSin.mul(0.203))));
    colour = vec3(red, green, blue);

    const temperature = float(this.uniforms.temperature).mul(0.5);
    const tint = float(this.uniforms.tint).mul(0.5);
    const whiteBalance = vec3(
      float(1).add(temperature).add(tint.mul(0.5)),
      float(1).sub(tint),
      float(1).sub(temperature).add(tint.mul(0.5))
    );
    colour = max(colour.mul(whiteBalance), vec3(0));

    const liftAmount = float(this.uniforms.lift);
    const liftTintVec = convert(this.uniforms.liftTint, 'vec3') as Node<'vec3'>;
    const liftOffset = liftTintVec.sub(vec3(1, 1, 1)).mul(liftAmount);
    colour = max(colour.add(liftOffset), vec3(0));

    const safeGamma = max(float(0.001), float(this.uniforms.gamma));
    colour = max(colour, vec3(0.00001)).pow(vec3(float(1).div(safeGamma)));
    colour = colour.mul(convert(this.uniforms.gammaTint, 'vec3') as Node<'vec3'>);

    colour = colour.mul(float(this.uniforms.gain)).mul(convert(this.uniforms.gainTint, 'vec3') as Node<'vec3'>);

    const tonalLuma = dot(colour, LUMA_WEIGHTS);
    const shadowsMask = float(1).sub(smoothstep(float(0.20), float(0.55), tonalLuma));
    const highlightsMask = smoothstep(float(0.45), float(0.85), tonalLuma);
    const midtonesMask = max(float(0), float(1).sub(shadowsMask).sub(highlightsMask));

    const shadowsBlend = float(this.uniforms.shadowsStrength).mul(shadowsMask);
    const midtonesBlend = float(this.uniforms.midtonesStrength).mul(midtonesMask);
    const highlightsBlend = float(this.uniforms.highlightsStrength).mul(highlightsMask);

    colour = colour.mul(mix(vec3(1, 1, 1), this.uniforms.shadowsTint, shadowsBlend));
    colour = colour.mul(mix(vec3(1, 1, 1), this.uniforms.midtonesTint, midtonesBlend));
    colour = colour.mul(mix(vec3(1, 1, 1), this.uniforms.highlightsTint, highlightsBlend));

    return vec4(max(colour, vec3(0)), input.a);
  }

  override applyWebGPUConfig(_node: WebGPUEffectNode, config: ColorGradingEffectConfig): void {
    if (!this.uniforms) {
      return;
    }

    if (config.exposure !== undefined) this.uniforms.exposure.value = config.exposure;
    if (config.contrast !== undefined) this.uniforms.contrast.value = config.contrast;
    if (config.saturation !== undefined) this.uniforms.saturation.value = config.saturation;
    if (config.hueShift !== undefined) this.uniforms.hueShift.value = config.hueShift;
    if (config.temperature !== undefined) this.uniforms.temperature.value = config.temperature;
    if (config.tint !== undefined) this.uniforms.tint.value = config.tint;
    if (config.lift !== undefined) this.uniforms.lift.value = config.lift;
    if (config.gamma !== undefined) this.uniforms.gamma.value = config.gamma;
    if (config.gain !== undefined) this.uniforms.gain.value = config.gain;

    if (config.liftTint !== undefined) this.uniforms.liftTint.value.setHex(config.liftTint);
    if (config.gammaTint !== undefined) this.uniforms.gammaTint.value.setHex(config.gammaTint);
    if (config.gainTint !== undefined) this.uniforms.gainTint.value.setHex(config.gainTint);

    if (config.shadowsTint !== undefined) this.uniforms.shadowsTint.value.setHex(config.shadowsTint);
    if (config.midtonesTint !== undefined) this.uniforms.midtonesTint.value.setHex(config.midtonesTint);
    if (config.highlightsTint !== undefined) this.uniforms.highlightsTint.value.setHex(config.highlightsTint);

    if (config.shadowsStrength !== undefined) this.uniforms.shadowsStrength.value = config.shadowsStrength;
    if (config.midtonesStrength !== undefined) this.uniforms.midtonesStrength.value = config.midtonesStrength;
    if (config.highlightsStrength !== undefined) this.uniforms.highlightsStrength.value = config.highlightsStrength;
  }

  override preRender(context: EffectContext): void {
    if (!this.uniforms) return;

    if (context.sharedExclusionMaskTexture !== undefined) {
      this.uniforms.hasExclusions.value = context.sharedHasExclusions === true;
      return;
    }

    const excludedObjects = (context as { excludedObjects?: ReadonlySet<THREE.Object3D> }).excludedObjects;
    this.uniforms.hasExclusions.value = excludedObjects ? excludedObjects.size > 0 : false;
  }

  override requiresRebuild(oldConfig: ColorGradingEffectConfig, newConfig: ColorGradingEffectConfig): boolean {
    return oldConfig.enabled !== newConfig.enabled;
  }

  override dispose(): void {
    this.uniforms = null;
  }
}
